import { Howl } from 'howler';
import { Rng, freshSeed } from '@/core/rng';
import { settings } from '@/config/settings';

/** Crossfade length between tracks (ms) — no hard cuts (user request). */
const FADE_MS = 1400;
const MUSIC_VOLUME = 0.55;

/** Combat rotation — one random track per arena (garage is shop-only). */
const COMBAT_TRACKS = [
  'music/india_happy-war-drums-494866.mp3',
  'music/music_unlimited-space-120280.mp3',
  'music/nastelbom-epic-war-383905.mp3',
  'music/paulyudin-space-152377.mp3',
  'music/solarflex-epic-war-541542.mp3',
  'music/solarflex-space-541545.mp3',
  'music/the_mountain-cinematic-war-162218.mp3',
];
const SHOP_TRACK = 'music/garage.mp3';

/**
 * Music manager (Howler): random combat track per arena, garage.mp3 in the
 * upgrade bay, everything crossfaded. Own seeded rng — track choice must
 * never touch the gameplay stream (determinism).
 */
class MusicManager {
  private readonly rng = new Rng(freshSeed());
  private readonly cache = new Map<string, Howl>();
  private current: Howl | null = null;
  private currentSrc = '';
  private lastCombat = '';

  private howl(src: string): Howl {
    let h = this.cache.get(src);
    if (!h) {
      h = new Howl({ src: [src], loop: true, volume: 0, html5: true });
      this.cache.set(src, h);
    }
    return h;
  }

  /** Crossfade to `src`; no-op if it is already playing. */
  private swap(src: string): void {
    if (!settings.sound || this.currentSrc === src) return;
    const prev = this.current;
    if (prev) {
      prev.fade(prev.volume(), 0, FADE_MS);
      const dying = prev;
      setTimeout(() => dying.stop(), FADE_MS + 50);
    }
    const next = this.howl(src);
    next.stop();
    next.volume(0);
    next.play();
    next.fade(0, MUSIC_VOLUME, FADE_MS);
    this.current = next;
    this.currentSrc = src;
  }

  /** New arena → a fresh random combat track (never the same twice). */
  playCombat(): void {
    const pool = COMBAT_TRACKS.filter((s) => s !== this.lastCombat);
    const pick = pool[this.rng.int(0, pool.length - 1)]!;
    this.lastCombat = pick;
    this.swap(pick);
  }

  /** Upgrade bay → garage.mp3 (the hangar anthem). */
  playShop(): void {
    this.swap(SHOP_TRACK);
  }

  /** Sound toggled off in the settings → fade everything out. */
  sync(): void {
    if (!settings.sound && this.current) {
      this.current.fade(this.current.volume(), 0, 400);
      const dying = this.current;
      setTimeout(() => dying.stop(), 450);
      this.current = null;
      this.currentSrc = '';
    }
  }
}

export const music = new MusicManager();
